import { type QueryClient, useQuery } from '@tanstack/react-query';
import { ItemsApi } from '@/common/api/items';
import type { ItemFiltersType } from '@/common/api/types/itemFilters';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { isDateTimeFilterKey } from '@/common/zod-schemas/itemFilterSearchSchema';
import { TOP_LEVEL_TOTALS_ITEM_ID } from '@/features/insights-dashboard/constants';
import { getExclusiveRangeEndDate } from '@/features/insights-dashboard/utils/dateRangeUtils';

const transformDateTimeFilterToRSQL = (
  key: string,
  value: string[] | number | number[] | null | undefined,
  operator: string,
): string | null => {
  // Type guard: date/time filters should only have string values
  if (typeof value === 'number' || (Array.isArray(value) && typeof value[0] === 'number')) {
    return null;
  }
  if (operator === 'null' || operator === 'notnull') {
    const isNull = operator === 'null' ? 'true' : 'false';
    return `${key}=isnull=${isNull}`;
  }

  if (value === null || value === undefined) {
    return null;
  }

  // Handle array values (all values are already full ISO timestamps)
  if (Array.isArray(value)) {
    if (value.length === 0) return null;

    const stringValues = value.filter((v): v is string => typeof v === 'string');
    if (stringValues.length !== value.length) return null;

    if (operator === 'in' && stringValues.length === 2) {
      const startDate = stringValues[0];
      const endDate = stringValues[1];
      if (!startDate || !endDate) return null;
      const exclusiveEndDate = getExclusiveRangeEndDate(endDate);
      return `${key}=ge='${startDate}';${key}=lt='${exclusiveEndDate.toISOString()}'`;
    }

    // 'eq' operator: converts single date to inclusive range (>= date AND < date+1day)
    if (operator === 'eq' && stringValues.length >= 1) {
      const dateValue = stringValues[0];
      if (!dateValue) return null;
      const exclusiveEndDate = getExclusiveRangeEndDate(dateValue);
      return `${key}=ge='${dateValue}';${key}=lt='${exclusiveEndDate.toISOString()}'`;
    }

    // 'gt' operator: add 1 day to get "after" semantics with GTE
    if (operator === 'gt' && stringValues.length >= 1) {
      const dateValue = stringValues[0];
      if (!dateValue) return null;
      const date = new Date(dateValue);
      const nextDay = new Date(date);
      nextDay.setUTCDate(date.getUTCDate() + 1);
      return `${key}=ge='${nextDay.toISOString()}'`;
    }

    if (operator === 'lt' && stringValues.length >= 1) {
      const dateValue = stringValues[0];
      if (!dateValue) return null;
      return `${key}=lt='${dateValue}'`;
    }

    return null;
  }

  // All date values should be arrays; single values not supported
  return null;
};

const createFilterStringFromInsightsFilters = ({ filters }: { filters?: ItemFiltersType }) => {
  if (!filters || Object.keys(filters || {}).length === 0) {
    return '';
  }

  const { parentId, ...rest } = filters || {};
  let idFilters = '';
  if (parentId) {
    if (Array.isArray(parentId.value) && parentId.value.length > 0) {
      const parentIdsToFetch = parentId.value.map((id) => `'${id}'`).join(',');
      const itemIdsToFetch = parentId.value
        .filter((id) => Number(id) !== TOP_LEVEL_TOTALS_ITEM_ID)
        .map((id) => `'${id}'`)
        .join(',');
      const idMatcher = `id=in=(${itemIdsToFetch})`;
      const parentIdMatcher = `parentId=in=(${parentIdsToFetch})`;
      idFilters = idFilters.concat(`${parentIdMatcher},${idMatcher}`);
    } else {
      idFilters = idFilters.concat(`parentId=='${parentId.value}',id=='${parentId.value}'`);
    }
  }

  const otherFilters = Object.entries(rest || {})
    .map(([key, v]) => {
      if (isDateTimeFilterKey(key)) {
        // Date values should always be arrays after parsing/UI
        const value = Array.isArray(v.value) ? v.value : null;
        return transformDateTimeFilterToRSQL(key, value, v.operator || 'eq');
      }

      if (Array.isArray(v.value)) {
        if (v.value.length === 0) {
          return null;
        } else if (v.value.length === 1) {
          return `${key}=='${v.value[0]}'`;
        }
        return `${key}=in=(${v.value.map((v) => `'${v}'`).join(',')})`;
      }
      return `${key}==${v.value}`;
    })
    .filter(Boolean)
    .join(';');

  if (idFilters && otherFilters) {
    return `(${idFilters});${otherFilters}`;
  } else if (idFilters) {
    return idFilters;
  } else if (otherFilters) {
    return otherFilters;
  }
  return '';
};

export const invalidateInsightsItemsDataSet = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: commonQueryKeys.insightsItemsDataSet() });
};

export const useInsightsItemsDataSetQuery = (
  { filters }: { filters?: ItemFiltersType } = {},
  staleTime?: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...commonQueryKeys.insightsItemsDataSet(filters)],
    queryFn: () => {
      const filterString = createFilterStringFromInsightsFilters({ filters });
      return ItemsApi.getInsightsItemsDataSet({ filterString });
    },
    enabled,
    retry: false,
    ...(staleTime !== undefined && { staleTime }),
  });
};
